/**
 * src/offline/useConnectivity.ts — WO-M3
 *
 * Connectivity detection and auto-sync trigger.
 *
 * APPROACH
 * --------
 * The platform connectivity shim isOnline() reports reachability (native + web) but has no
 * subscription API. We combine two sources to detect online/offline transitions:
 *
 * 1. AppState (React Native core) — fires when the app moves between
 *    background ↔ foreground. On each foreground transition we poll
 *    `getNetworkStateAsync()` to snapshot the current reachability.
 *
 * 2. Interval polling — while the app is active, poll every POLL_INTERVAL_MS
 *    so we detect transitions that happen without an app-state change (e.g.
 *    the device walks into a dead zone while the app is in the foreground).
 *
 * When the state transitions from offline → online, `syncQueue()` is called
 * automatically. Manual sync is exposed via the returned `syncNow` function.
 *
 * EXPORTS
 * -------
 * useConnectivity() — React hook; mounts polling + AppState listener.
 *   Returns: { isOnline, isSyncing, syncNow, pendingCount, failedCount }
 *
 * NEVER imports @smartbox/* (CLAUDE.md invariant 1).
 * No PII in logs.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { syncQueue, getQueueCounts } from './captureQueue';
import { isOnline as platformIsOnline } from '../platform/connectivity';

/** Poll interval while the app is in the foreground (ms). */
const POLL_INTERVAL_MS = 15_000;

export interface ConnectivityState {
  /** True when the device reports internet reachability. */
  isOnline: boolean;
  /** True while syncQueue() is running. */
  isSyncing: boolean;
  /** Trigger an immediate sync attempt. Safe to call when offline (no-ops). */
  syncNow: () => Promise<void>;
  /** Rows in 'pending' or 'syncing' state (not yet uploaded). */
  pendingCount: number;
  /** Rows that have exhausted retries. */
  failedCount: number;
}

async function checkOnline(): Promise<boolean> {
  return platformIsOnline();
}

export function useConnectivity(): ConnectivityState {
  const [isOnline, setIsOnline] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  // Use a ref for the previous online state so the transition detector in the
  // interval/AppState callbacks has a stable reference without stale closure.
  const wasOnlineRef = useRef(false);
  const isSyncingRef = useRef(false);

  const refreshCounts = useCallback(async () => {
    try {
      const counts = await getQueueCounts();
      setPendingCount(counts.pending + counts.syncing);
      setFailedCount(counts.failed);
    } catch {
      // Non-fatal — counts may be stale but the queue state is not affected.
    }
  }, []);

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return; // prevent re-entrant sync
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      await syncQueue();
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
      await refreshCounts();
    }
  }, [refreshCounts]);

  const pollOnce = useCallback(async () => {
    const online = await checkOnline();
    setIsOnline(online);
    await refreshCounts();

    // Transition: offline → online triggers auto-sync.
    if (online && !wasOnlineRef.current) {
      wasOnlineRef.current = true;
      await runSync();
    } else {
      wasOnlineRef.current = online;
    }
  }, [refreshCounts, runSync]);

  const syncNow = useCallback(async () => {
    const online = await checkOnline();
    setIsOnline(online);
    if (!online) return; // nothing to sync when offline
    await runSync();
  }, [runSync]);

  useEffect(() => {
    // Run once on mount.
    pollOnce();

    // Interval polling while the app is active.
    const intervalId = setInterval(pollOnce, POLL_INTERVAL_MS);

    // Re-check on foreground transitions.
    function handleAppStateChange(nextState: AppStateStatus) {
      if (nextState === 'active') {
        pollOnce();
      }
    }
    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      clearInterval(intervalId);
      subscription.remove();
    };
  }, [pollOnce]);

  return { isOnline, isSyncing, syncNow, pendingCount, failedCount };
}
